import { create } from 'zustand';
import { REPUTACION_INICIAL, type FactionId } from '@/types';
import type { WorldState, LocationId, PendingConsequence } from '@/types';
import { cargar, guardar } from '@/engine/persistence';

interface WorldStoreState extends WorldState {
  cargado: boolean;
  desbloquearLocacion: (id: LocationId) => void;
  irALocacion: (id: LocationId) => void;
  modificarReputacion: (facción: FactionId, delta: number) => void;
  setFlag: (id: string, valor: boolean) => void;
  agregarConsecuenciaPendiente: (c: PendingConsequence) => void;
  resolverConsecuenciaPendiente: (id: string) => void;
  reiniciar: () => void;
  cargarDesdeStorage: () => void;
  persistir: () => void;
}

const ESTADO_INICIAL: WorldState = {
  locacionActual: 'hogar_ruinas',
  locacionesDesbloqueadas: ['hogar_ruinas'],
  reputacion: { ...REPUTACION_INICIAL },
  flags: {},
  consecuenciasPendientes: [],
};

export const useWorldStore = create<WorldStoreState>((set, get) => ({
  ...ESTADO_INICIAL,
  cargado: false,

  desbloquearLocacion: (id) => {
    set((s) =>
      s.locacionesDesbloqueadas.includes(id)
        ? s
        : { locacionesDesbloqueadas: [...s.locacionesDesbloqueadas, id] },
    );
    get().persistir();
  },

  irALocacion: (id) => {
    set({ locacionActual: id });
    get().persistir();
  },

  modificarReputacion: (facción, delta) => {
    set((s) => ({
      reputacion: {
        ...s.reputacion,
        [facción]: Math.max(-100, Math.min(100, s.reputacion[facción] + delta)),
      },
    }));
    get().persistir();
  },

  setFlag: (id, valor) => {
    set((s) => ({ flags: { ...s.flags, [id]: valor } }));
    get().persistir();
  },

  agregarConsecuenciaPendiente: (c) => {
    set((s) => ({ consecuenciasPendientes: [...s.consecuenciasPendientes, c] }));
    get().persistir();
  },

  resolverConsecuenciaPendiente: (id) => {
    set((s) => ({
      consecuenciasPendientes: s.consecuenciasPendientes.filter((c) => c.id !== id),
    }));
    get().persistir();
  },

  reiniciar: () => {
    set({ ...ESTADO_INICIAL, cargado: true });
    get().persistir();
  },

  cargarDesdeStorage: () => {
    const r = cargar<WorldState>('world');
    if (r.status === 'ok') {
      set({ ...r.valor, cargado: true });
    } else {
      set({ cargado: true });
    }
  },

  persistir: () => {
    const s = get();
    guardar('world', {
      locacionActual: s.locacionActual,
      locacionesDesbloqueadas: s.locacionesDesbloqueadas,
      reputacion: s.reputacion,
      flags: s.flags,
      consecuenciasPendientes: s.consecuenciasPendientes,
    });
  },
}));
